import { CheckCircle, ChevronDown, ChevronRight, Copy, Eye, EyeOff, Globe, Key, Loader2, Plus, Send, Settings, Trash2, X, Zap } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react';
import { useIsDark } from '../../contexts/ThemeContext';
import { useWorkflowContext } from '../../contexts/WorkflowContext';
import { useNodeValidation } from '../../hooks/useNodeValidation';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import type { CustomNodeData } from '../../types/nodes';
import { VariablePicker } from '../VariablePicker';

interface WebhookCallerConfigProps {
  nodeId: string;
  data: CustomNodeData;
  onChange: (data: CustomNodeData) => void;
  onWizardStateChange?: (state: {
    wizardMode: boolean;
    currentStep: number;
    totalSteps: number;
    canProceedToNext: boolean;
    onPreviousStep: () => void;
    onNextStep: () => void;
    onCompleteWizard: () => Promise<void>;
    validationAttempted: boolean;
    validationFailed: boolean;
  }) => void;
  onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

interface WebhookCallerConfig {
  // Step 1: Method & URL
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  
  // Step 2: Headers & Auth
  headers: { key: string; value: string; enabled: boolean }[];
  authentication: {
    type: 'none' | 'basic' | 'bearer' | 'apikey';
    username?: string;
    password?: string;
    token?: string;
    apiKey?: string;
    apiKeyLocation?: 'header' | 'query';
    apiKeyName?: string;
  };
  
  // Step 3: Request Body
  bodyType: 'none' | 'json' | 'form' | 'text' | 'raw';
  jsonBody: {
    mode: 'visual' | 'raw';
    visualFields: JsonField[];
    rawJson: string;
  };
  formBody: { key: string; value: string; enabled: boolean }[];
  textBody: string;
  rawBody: string;
  
  // Step 4: Options & Testing
  timeout: number;
  retries: number;
  async: boolean;
  followRedirects: boolean;
  validateSSL: boolean;
  passThrough: boolean; // Include upstream data automatically
  storeAs?: string; // Custom storage key
  
  // Testing
  lastTestResult?: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: any;
    responseTime: number;
    timestamp: string;
  };
}

interface JsonField {
  id: string;
  key: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  value: string;
  enabled: boolean;
  children?: JsonField[];
  collapsed?: boolean;
}

const defaultConfig: WebhookCallerConfig = {
  method: 'POST',
  url: '',
  headers: [],
  authentication: { type: 'none' },
  bodyType: 'json',
  jsonBody: {
    mode: 'visual',
    visualFields: [],
    rawJson: '{\n  \n}'
  },
  formBody: [],
  textBody: '',
  rawBody: '',
  timeout: 30000,
  retries: 0,
  async: false,
  followRedirects: true,
  validateSSL: true,
  passThrough: false
};

const EXAMPLE_WEBHOOKS = [
  {
    name: 'Slack Notification',
    config: {
      method: 'POST' as const,
      url: 'https://hooks.slack.com/services/YOUR/SLACK/WEBHOOK',
      bodyType: 'json' as const,
      jsonBody: {
        mode: 'visual' as const,
        visualFields: [
          { id: '1', key: 'channel', type: 'string' as const, value: '#general', enabled: true },
          { id: '2', key: 'username', type: 'string' as const, value: 'Workflow Bot', enabled: true },
          { id: '3', key: 'text', type: 'string' as const, value: 'New submission from {{submission.name}}', enabled: true }
        ],
        rawJson: ''
      }
    }
  },
  {
    name: 'Webhook.site Test',
    config: {
      method: 'POST' as const,
      url: 'https://webhook.site/unique-id',
      bodyType: 'json' as const,
      jsonBody: {
        mode: 'visual' as const,
        visualFields: [
          { id: '1', key: 'event', type: 'string' as const, value: 'form_submission', enabled: true },
          { id: '2', key: 'data', type: 'object' as const, value: '{{submission}}', enabled: true },
          { id: '3', key: 'timestamp', type: 'string' as const, value: '{{timestamp}}', enabled: true }
        ],
        rawJson: ''
      }
    }
  },
  {
    name: 'REST API Call',
    config: {
      method: 'POST' as const,
      url: 'https://api.example.com/v1/customers',
      headers: [
        { key: 'Content-Type', value: 'application/json', enabled: true },
        { key: 'X-API-Version', value: '1.0', enabled: true }
      ],
      authentication: { type: 'bearer' as const, token: '{{api_token}}' },
      bodyType: 'json' as const,
      jsonBody: {
        mode: 'visual' as const,
        visualFields: [
          { id: '1', key: 'name', type: 'string' as const, value: '{{submission.businessName}}', enabled: true },
          { id: '2', key: 'email', type: 'string' as const, value: '{{submission.email}}', enabled: true },
          { id: '3', key: 'metadata', type: 'object' as const, value: '{{transform.enrichedData}}', enabled: true }
        ],
        rawJson: ''
      }
    }
  }
];

export function WebhookCallerConfig({ nodeId, data, onChange, onWizardStateChange, onValidationRequest }: WebhookCallerConfigProps) {
  const { validateNode, isValidating, lastValidationResult, clearValidationResult } = useNodeValidation();

  // Validation state for two-step complete setup
  const [validationAttempted, setValidationAttempted] = useState(false); // Track if validation has been attempted
  const [validationFailed, setValidationFailed] = useState(false); // Track if validation failed on previous attempt

  const { getFlattenedFields } = useWorkflowContext();
  const isDark = useIsDark();
  const [config, setConfig] = useState<WebhookCallerConfig>(() => {
    const existingConfig = data.config as Partial<WebhookCallerConfig>;
    return {
      ...defaultConfig,
      ...existingConfig,
      storeAs: existingConfig?.storeAs || nodeId
    };
  });

  // Wizard state
  const [wizardMode, setWizardMode] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['method-url']));

  // Get available fields for token insertion
  const availableFields = getFlattenedFields(nodeId);

  // Wizard steps
  const wizardSteps = [
    { id: 1, title: 'Method & URL', description: 'Configure the HTTP method and target URL', icon: Globe },
    { id: 2, title: 'Headers & Auth', description: 'Set up authentication and custom headers', icon: Key },
    { id: 3, title: 'Request Body', description: 'Configure the data to send in the request', icon: Settings },
    { id: 4, title: 'Preview & Test', description: 'Review configuration and test the webhook', icon: Send }
  ];

  // Validation functions
  const isStep1Valid = () => {
    return config.url.trim() !== '';
  };

  const isStep2Valid = () => {
    if (config.authentication.type === 'basic') {
      return !!(config.authentication.username && config.authentication.password);
    }
    if (config.authentication.type === 'bearer') {
      return !!config.authentication.token;
    }
    if (config.authentication.type === 'apikey') {
      return !!(config.authentication.apiKey && config.authentication.apiKeyName);
    }
    return true; // 'none' auth is always valid
  };

  const isStep3Valid = () => {
    if (config.bodyType === 'none') return true;
    if (config.bodyType === 'json') {
      if (config.jsonBody.mode === 'visual') {
        return config.jsonBody.visualFields.length > 0;
      }
      try {
        JSON.parse(config.jsonBody.rawJson);
        return true;
      } catch {
        return false;
      }
    }
    return true;
  };

  const canProceedToStep = (step: number) => {
    switch (step) {
      case 2: return isStep1Valid();
      case 3: return isStep1Valid() && isStep2Valid();
      case 4: return isStep1Valid() && isStep2Valid() && isStep3Valid();
      default: return true;
    }
  };

	// Validation function that can be called by parent components
	const performValidation = useCallback(async (): Promise<boolean> => {
		try {
			const result = await validateNode(nodeId, data);
			
			// Return true if validation passed, false if there are critical errors
			return result.valid || !result.errors.some(error => error.severity === 'error');
		} catch (error) {
			console.error('Validation failed:', error);
			return false;
		}
	}, [validateNode, nodeId, data]);

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

  // Handle configuration changes
  const handleChange = (newConfig: Partial<WebhookCallerConfig>) => {
    const updatedConfig = { ...config, ...newConfig };
    setConfig(updatedConfig);
    onChange({
      ...data,
      config: updatedConfig,
    });
  };

  // Wizard state communication to parent
  useEffect(() => {
    if (onWizardStateChange) {
      onWizardStateChange({
        wizardMode,
        currentStep,
        totalSteps: wizardSteps.length,
        canProceedToNext: canProceedToStep(currentStep + 1),
        onPreviousStep: () => setCurrentStep(Math.max(1, currentStep - 1)),
        onNextStep: () => setCurrentStep(currentStep + 1),
        onCompleteWizard: async () => {
					// Step 1: If validation hasn't been attempted yet, run validation and stay in node
					if (!validationAttempted) {
						try {
							const result = await validateNode(nodeId, data);
							setValidationAttempted(true);
							
							if (!result.valid) {
								// Validation failed - stay in node and show errors
								setValidationFailed(true);
								console.log('Validation failed, staying in node:', result);
								return; // Don't exit the node
							} else {
								// Validation succeeded - proceed to exit
								setValidationFailed(false);
								console.log('Validation succeeded:', result);
							}
						} catch (error) {
							console.error('Validation failed:', error);
							setValidationAttempted(true);
							setValidationFailed(true);
							return; // Don't exit the node on error
						}
					}
					
					// Step 2: If validation was already attempted (regardless of success/failure), exit the node
					// This allows users to force exit even if validation failed
					
					onChange({
						...data,
						config,
					});
					setWizardMode(false);
					
					// Reset validation state for next time
					setValidationAttempted(false);
					setValidationFailed(false);
				},
				validationAttempted,
				validationFailed,
      });
    }
  }, [wizardMode, currentStep, config, data, onChange, onWizardStateChange, validationAttempted, validationFailed, validateNode, nodeId]);

  // Section toggle
  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  // Load example configuration
  const loadExample = (example: typeof EXAMPLE_WEBHOOKS[0]) => {
    handleChange({
      ...example.config,
      headers: example.config.headers || [],
      authentication: example.config.authentication || { type: 'none' }
    });
  };

  // Generate unique ID for fields
  const generateFieldId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  // JSON Field management
  const addJsonField = (parentPath?: string) => {
    const newField: JsonField = {
      id: generateFieldId(),
      key: '',
      type: 'string',
      value: '',
      enabled: true
    };

    if (parentPath) {
      // Adding to nested object - implement nested field logic
      // For now, just add to root
    }

    handleChange({
      jsonBody: {
        ...config.jsonBody,
        visualFields: [...config.jsonBody.visualFields, newField]
      }
    });
  };

  const updateJsonField = (fieldId: string, updates: Partial<JsonField>) => {
    const updateField = (fields: JsonField[]): JsonField[] => {
      return fields.map(field => {
        if (field.id === fieldId) {
          return { ...field, ...updates };
        }
        if (field.children) {
          return { ...field, children: updateField(field.children) };
        }
        return field;
      });
    };

    handleChange({
      jsonBody: {
        ...config.jsonBody,
        visualFields: updateField(config.jsonBody.visualFields)
      }
    });
  };

  const removeJsonField = (fieldId: string) => {
    const removeField = (fields: JsonField[]): JsonField[] => {
      return fields.filter(field => {
        if (field.id === fieldId) return false;
        if (field.children) {
          field.children = removeField(field.children);
        }
        return true;
      });
    };

    handleChange({
      jsonBody: {
        ...config.jsonBody,
        visualFields: removeField(config.jsonBody.visualFields)
      }
    });
  };

  // Convert visual fields to JSON
  const generateJsonFromVisualFields = (): string => {
    const buildObject = (fields: JsonField[]): any => {
      const obj: any = {};
      fields.forEach(field => {
        if (!field.enabled || !field.key) return;
        
        switch (field.type) {
          case 'string':
            obj[field.key] = field.value;
            break;
          case 'number':
            obj[field.key] = isNaN(Number(field.value)) ? field.value : Number(field.value);
            break;
          case 'boolean':
            obj[field.key] = field.value === 'true';
            break;
          case 'object':
            if (field.children && field.children.length > 0) {
              obj[field.key] = buildObject(field.children);
            } else {
              // Try to parse as token or JSON
              try {
                obj[field.key] = field.value.startsWith('{{') ? field.value : JSON.parse(field.value);
              } catch {
                obj[field.key] = field.value;
              }
            }
            break;
          case 'array':
            try {
              obj[field.key] = field.value.startsWith('{{') ? field.value : JSON.parse(field.value);
            } catch {
              obj[field.key] = field.value.split(',').map(v => v.trim());
            }
            break;
        }
      });
      return obj;
    };

    try {
      return JSON.stringify(buildObject(config.jsonBody.visualFields), null, 2);
    } catch (error) {
      return '{\n  "error": "Invalid field configuration"\n}';
    }
  };

  // Test webhook functionality
  const testWebhook = async () => {
    setIsLoading(true);
    const startTime = Date.now();

    try {
      // Build request body based on type
      let requestBody = null;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      // Add custom headers
      config.headers.forEach(header => {
        if (header.enabled && header.key && header.value) {
          headers[header.key] = header.value;
        }
      });

      // Add authentication headers
      if (config.authentication.type === 'bearer' && config.authentication.token) {
        headers['Authorization'] = `Bearer ${config.authentication.token}`;
      } else if (config.authentication.type === 'apikey' && config.authentication.apiKey) {
        if (config.authentication.apiKeyLocation === 'header') {
          headers[config.authentication.apiKeyName || 'X-API-Key'] = config.authentication.apiKey;
        }
      }

      // Build request body
      if (config.method !== 'GET') {
        switch (config.bodyType) {
          case 'json':
            if (config.jsonBody.mode === 'visual') {
              requestBody = generateJsonFromVisualFields();
            } else {
              requestBody = config.jsonBody.rawJson;
            }
            break;
          case 'form':
            const formData = new FormData();
            config.formBody.forEach(field => {
              if (field.enabled && field.key && field.value) {
                formData.append(field.key, field.value);
              }
            });
            requestBody = formData;
            headers['Content-Type'] = 'application/x-www-form-urlencoded';
            break;
          case 'text':
            requestBody = config.textBody;
            headers['Content-Type'] = 'text/plain';
            break;
          case 'raw':
            requestBody = config.rawBody;
            break;
        }
      }

      // Simulate API call (in real implementation, this would make actual HTTP request)
      const mockResponse = {
        status: 200,
        statusText: 'OK',
        headers: {
          'Content-Type': 'application/json',
          'X-Response-Time': '150ms'
        },
        body: {
          success: true,
          message: 'Webhook received successfully',
          data: {
            id: '12345',
            timestamp: new Date().toISOString(),
            processed: true
          }
        },
        responseTime: Date.now() - startTime,
        timestamp: new Date().toISOString()
      };

      handleChange({ lastTestResult: mockResponse });
    } catch (error) {
      const errorResult = {
        status: 500,
        statusText: 'Internal Server Error',
        headers: {},
        body: { error: error instanceof Error ? error.message : 'Unknown error' },
        responseTime: Date.now() - startTime,
        timestamp: new Date().toISOString()
      };
      handleChange({ lastTestResult: errorResult });
    } finally {
      setIsLoading(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return null;
    }
  };

  // Step 1: Method & URL
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">HTTP Method & Target URL</h3>
        <p className="text-sm text-blue-800 dark:text-blue-200">
          Configure where and how to send your webhook request. The URL can include variables from upstream nodes.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            HTTP Method
          </label>
          <select
            value={config.method}
            onChange={(e) => handleChange({ method: e.target.value as WebhookCallerConfig['method'] })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>

        <div className="md:col-span-3 space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Webhook URL <span className="text-red-500">*</span>
          </label>
          <VariablePicker
            nodeId={nodeId}
            value={config.url}
            onChange={(value) => handleChange({ url: value })}
            placeholder="https://api.example.com/webhook or https://webhook.site/{{unique_id}}"
            className="text-sm"
          />
        </div>
      </div>

      {/* Example Templates */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Zap className="w-4 h-4" />
          Quick Start Templates
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {EXAMPLE_WEBHOOKS.map((example) => (
            <button
              key={example.name}
              onClick={() => loadExample(example)}
              className="p-3 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{example.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {example.config.method} {example.config.url.split('/')[2] || 'Custom URL'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* URL Preview */}
      {config.url && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">URL Preview</h4>
          <div className="flex items-center gap-3">
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              config.method === 'GET' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
              config.method === 'POST' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
              config.method === 'PUT' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
              config.method === 'PATCH' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}>
              {config.method}
            </span>
            <code className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 px-2 py-1 rounded border">
              {config.url}
            </code>
          </div>
        </div>
      )}
    </div>
  );

  // Step 2: Headers & Auth
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
        <h3 className="font-medium text-amber-900 dark:text-amber-100 mb-2">Authentication & Headers</h3>
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Configure authentication and add custom headers for your webhook request.
        </p>
      </div>

      {/* Authentication */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Authentication</h4>
        <div className="space-y-3">
          <select
            value={config.authentication.type}
            onChange={(e) => handleChange({ 
              authentication: { 
                ...config.authentication, 
                type: e.target.value as WebhookCallerConfig['authentication']['type']
              } 
            })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="none">No Authentication</option>
            <option value="basic">Basic Auth</option>
            <option value="bearer">Bearer Token</option>
            <option value="apikey">API Key</option>
          </select>

          {config.authentication.type === 'basic' && (
            <div className="grid grid-cols-2 gap-3">
              <VariablePicker
                nodeId={nodeId}
                value={config.authentication.username || ''}
                onChange={(value) => handleChange({ 
                  authentication: { ...config.authentication, username: value } 
                })}
                placeholder="Username"
                className="text-sm"
              />
              <VariablePicker
                nodeId={nodeId}
                value={config.authentication.password || ''}
                onChange={(value) => handleChange({ 
                  authentication: { ...config.authentication, password: value } 
                })}
                placeholder="Password"
                className="text-sm"
              />
            </div>
          )}

          {config.authentication.type === 'bearer' && (
            <VariablePicker
              nodeId={nodeId}
              value={config.authentication.token || ''}
              onChange={(value) => handleChange({ 
                authentication: { ...config.authentication, token: value } 
              })}
              placeholder="Bearer token (e.g., {{api_token}})"
              className="text-sm"
            />
          )}

          {config.authentication.type === 'apikey' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <VariablePicker
                  nodeId={nodeId}
                  value={config.authentication.apiKeyName || ''}
                  onChange={(value) => handleChange({ 
                    authentication: { ...config.authentication, apiKeyName: value } 
                  })}
                  placeholder="Header/Parameter name (e.g., X-API-Key)"
                  className="text-sm"
                />
                <VariablePicker
                  nodeId={nodeId}
                  value={config.authentication.apiKey || ''}
                  onChange={(value) => handleChange({ 
                    authentication: { ...config.authentication, apiKey: value } 
                  })}
                  placeholder="API key value"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Location</label>
                <select
                  value={config.authentication.apiKeyLocation || 'header'}
                  onChange={(e) => handleChange({ 
                    authentication: { 
                      ...config.authentication, 
                      apiKeyLocation: e.target.value as 'header' | 'query'
                    } 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="header">Header</option>
                  <option value="query">Query Parameter</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Headers */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom Headers</h4>
          <button
            onClick={() => handleChange({ 
              headers: [...config.headers, { key: '', value: '', enabled: true }] 
            })}
            className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-600"
          >
            <Plus className="w-4 h-4" />
            Add Header
          </button>
        </div>

        {config.headers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <p className="text-sm">No custom headers configured</p>
            <p className="text-xs mt-1">Add headers for API authentication, content type, etc.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {config.headers.map((header, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <input
                  type="checkbox"
                  checked={header.enabled}
                  onChange={(e) => {
                    const newHeaders = [...config.headers];
                    newHeaders[index] = { ...header, enabled: e.target.checked };
                    handleChange({ headers: newHeaders });
                  }}
                  className="rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={header.key}
                    onChange={(e) => {
                      const newHeaders = [...config.headers];
                      newHeaders[index] = { ...header, key: e.target.value };
                      handleChange({ headers: newHeaders });
                    }}
                    placeholder="Header name (e.g., Content-Type)"
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
                  />
                  <VariablePicker
                    nodeId={nodeId}
                    value={header.value}
                    onChange={(value) => {
                      const newHeaders = [...config.headers];
                      newHeaders[index] = { ...header, value };
                      handleChange({ headers: newHeaders });
                    }}
                    placeholder="Header value"
                    className="text-sm"
                  />
                </div>
                <button
                  onClick={() => {
                    const newHeaders = config.headers.filter((_, i) => i !== index);
                    handleChange({ headers: newHeaders });
                  }}
                  className="p-1 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Step 3: Request Body
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
        <h3 className="font-medium text-green-900 dark:text-green-100 mb-2">Request Body Configuration</h3>
        <p className="text-sm text-green-800 dark:text-green-200">
          Configure what data to send in your webhook request. Use variables to include dynamic content from upstream nodes.
        </p>
      </div>

      {/* Body Type Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Request Body Type</label>
        <select
          value={config.bodyType}
          onChange={(e) => handleChange({ bodyType: e.target.value as WebhookCallerConfig['bodyType'] })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="none">No Body (for GET requests)</option>
          <option value="json">JSON</option>
          <option value="form">Form Data</option>
          <option value="text">Plain Text</option>
          <option value="raw">Raw Data</option>
        </select>
      </div>

      {/* JSON Body Builder */}
      {config.bodyType === 'json' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">JSON Body</h4>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleChange({ 
                  jsonBody: { 
                    ...config.jsonBody, 
                    mode: config.jsonBody.mode === 'visual' ? 'raw' : 'visual' 
                  } 
                })}
                className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                {config.jsonBody.mode === 'visual' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                {config.jsonBody.mode === 'visual' ? 'Raw JSON' : 'Visual Builder'}
              </button>
            </div>
          </div>

          {config.jsonBody.mode === 'visual' ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">JSON Fields</span>
                <button
                  onClick={() => addJsonField()}
                  className="flex items-center gap-1 px-3 py-1 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-600"
                >
                  <Plus className="w-4 h-4" />
                  Add Field
                </button>
              </div>

              {config.jsonBody.visualFields.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <p className="text-sm">No JSON fields configured</p>
                  <p className="text-xs mt-1">Add fields to build your JSON payload</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {config.jsonBody.visualFields.map((field) => (
                    <div key={field.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <input
                        type="checkbox"
                        checked={field.enabled}
                        onChange={(e) => updateJsonField(field.id, { enabled: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                      />
                      <div className="flex-1 grid grid-cols-3 gap-3">
                        <input
                          type="text"
                          value={field.key}
                          onChange={(e) => updateJsonField(field.id, { key: e.target.value })}
                          placeholder="Field name"
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
                        />
                        <select
                          value={field.type}
                          onChange={(e) => updateJsonField(field.id, { type: e.target.value as JsonField['type'] })}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                        >
                          <option value="string">String</option>
                          <option value="number">Number</option>
                          <option value="boolean">Boolean</option>
                          <option value="object">Object</option>
                          <option value="array">Array</option>
                        </select>
                        <VariablePicker
                          nodeId={nodeId}
                          value={field.value}
                          onChange={(value) => updateJsonField(field.id, { value })}
                          placeholder="Value or {{variable}}"
                          className="text-sm"
                        />
                      </div>
                      <button
                        onClick={() => removeJsonField(field.id)}
                        className="p-1 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* JSON Preview */}
              {config.jsonBody.visualFields.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Generated JSON Preview</h5>
                  <pre className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 p-3 rounded border overflow-x-auto">
                    {generateJsonFromVisualFields()}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <VariablePicker
                nodeId={nodeId}
                value={config.jsonBody.rawJson}
                onChange={(value) => handleChange({ 
                  jsonBody: { ...config.jsonBody, rawJson: value } 
                })}
                placeholder='{\n  "field": "{{variable}}",\n  "data": {\n    "nested": "value"\n  }\n}'
                className="text-sm font-mono"
                multiline
              />
              {config.jsonBody.rawJson && (() => {
                try {
                  JSON.parse(config.jsonBody.rawJson);
                  return (
                    <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Valid JSON format
                    </div>
                  );
                } catch {
                  return (
                    <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                      <X className="w-4 h-4" />
                      Invalid JSON format
                    </div>
                  );
                }
              })()}
            </div>
          )}
        </div>
      )}

      {/* Other body types can be implemented similarly */}
      {config.bodyType === 'text' && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Text Content</label>
          <VariablePicker
            nodeId={nodeId}
            value={config.textBody}
            onChange={(value) => handleChange({ textBody: value })}
            placeholder="Enter text content with {{variables}}"
            className="text-sm"
            multiline
          />
        </div>
      )}

      {/* Advanced Options */}
      <div className="space-y-4">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        >
          {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Advanced Options
        </button>

        {showAdvanced && (
          <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={config.passThrough}
                onChange={(e) => handleChange({ passThrough: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
              <span className="text-gray-700 dark:text-gray-300">Include upstream data automatically</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
              Automatically include all upstream context data in the request
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Step 4: Preview & Test
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
        <h3 className="font-medium text-purple-900 dark:text-purple-100 mb-2">Preview & Test</h3>
        <p className="text-sm text-purple-800 dark:text-purple-200">
          Review your webhook configuration and test it with sample data.
        </p>
      </div>

      {/* Request Preview */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Request Preview</h4>
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              config.method === 'GET' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
              config.method === 'POST' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
              'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
            }`}>
              {config.method}
            </span>
            <code className="text-sm text-gray-600 dark:text-gray-400">{config.url}</code>
          </div>

          {config.headers.filter(h => h.enabled).length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Headers:</h5>
              <div className="space-y-1">
                {config.headers.filter(h => h.enabled).map((header, index) => (
                  <div key={index} className="flex items-center gap-3 text-xs">
                    <code className="text-gray-500 dark:text-gray-400">{header.key}:</code>
                    <code className="text-gray-600 dark:text-gray-300">{header.value}</code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {config.bodyType !== 'none' && (
            <div>
              <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Body:</h5>
              <pre className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 p-3 rounded border overflow-x-auto max-h-40">
                {config.bodyType === 'json' && config.jsonBody.mode === 'visual' 
                  ? generateJsonFromVisualFields()
                  : config.bodyType === 'json' 
                  ? config.jsonBody.rawJson
                  : config.bodyType === 'text'
                  ? config.textBody
                  : config.rawBody
                }
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Test Button */}
      <div className="flex items-center gap-3">
        <button
          onClick={testWebhook}
          disabled={isLoading || !isStep1Valid()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {isLoading ? 'Testing...' : 'Test Webhook'}
        </button>
        
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={config.async}
            onChange={(e) => handleChange({ async: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
          Fire and forget (async)
        </label>
      </div>

      {/* Test Results */}
      {config.lastTestResult && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Test Results</h4>
          <div className={`border rounded-lg p-4 ${
            config.lastTestResult.status >= 200 && config.lastTestResult.status < 300
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
          }`}>
            <div className="flex items-center gap-3 mb-3">
              {config.lastTestResult.status >= 200 && config.lastTestResult.status < 300 ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              ) : (
                <X className="w-5 h-5 text-red-600 dark:text-red-400" />
              )}
              <span className="font-medium">
                {config.lastTestResult.status} {config.lastTestResult.statusText}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                ({config.lastTestResult.responseTime}ms)
              </span>
            </div>

            <div className="space-y-3">
              <div>
                <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Response Body:</h5>
                <pre className="text-xs bg-white dark:bg-gray-900 p-3 rounded border overflow-x-auto max-h-40">
                  {JSON.stringify(config.lastTestResult.body, null, 2)}
                </pre>
              </div>

              {Object.keys(config.lastTestResult.headers).length > 0 && (
                <div>
                  <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Response Headers:</h5>
                  <div className="space-y-1">
                    {Object.entries(config.lastTestResult.headers).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-3 text-xs">
                        <code className="text-gray-500 dark:text-gray-400">{key}:</code>
                        <code className="text-gray-600 dark:text-gray-300">{value}</code>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Available Variables */}
      {availableFields.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3">✨ Available Variables</h4>
          <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
            These variables from upstream nodes can be used in your webhook configuration:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {availableFields.slice(0, 8).map((field) => (
              <div key={field.id} className="flex justify-between items-center bg-white dark:bg-blue-950 p-2 rounded">
                <code className="text-sm text-blue-600 dark:text-blue-400">
                  {`{{${field.id}}}`}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(`{{${field.id}}}`)}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  title="Copy variable"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {availableFields.length > 8 && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
              +{availableFields.length - 8} more variables available
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4 space-y-6 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Webhook Caller Configuration</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Send HTTP requests to external APIs and webhooks with dynamic data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-blue-600 dark:text-blue-400">
            🧙‍♂️ Wizard Mode
          </span>
        </div>
      </div>

      {/* Validation Results */}
      {(isValidating || lastValidationResult) && (
        <ValidationResultDisplay
          errors={lastValidationResult?.errors || []}
          isValidating={isValidating}
          onDismiss={clearValidationResult}
        />
      )}

      {/* Wizard Progress */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Step {currentStep} of {wizardSteps.length}: {wizardSteps[currentStep - 1]?.title}
          </h4>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {Math.round((currentStep / wizardSteps.length) * 100)}% Complete
          </span>
        </div>

        <div className="flex gap-2">
          {wizardSteps.map((step) => {
            const StepIcon = step.icon;
            const isCompleted = currentStep > step.id;
            const isCurrent = currentStep === step.id;
            const canAccess = canProceedToStep(step.id);

            return (
              <button
                key={step.id}
                onClick={() => canAccess && setCurrentStep(step.id)}
                disabled={!canAccess}
                className={`flex-1 p-3 rounded-lg border text-left transition-colors ${
                  isCurrent
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-600 text-blue-900 dark:text-blue-100'
                    : isCompleted
                    ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-600 text-green-900 dark:text-green-100'
                    : canAccess
                    ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <StepIcon className="w-4 h-4" />
                  <span className="text-xs font-medium">{step.title}</span>
                  {isCompleted && <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 ml-auto" />}
                </div>
                <p className="text-xs opacity-75">{step.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="min-h-96">
        {renderStepContent()}
      </div>
    </div>
  );
} 